import ComputerIcon from '@mui/icons-material/Computer';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import { Button, Card, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import type { AdminUserSession } from '../../../types/auth';

type SessionInfoProps = {
  sessions: AdminUserSession[];
  isLoading: boolean;
  isLoggingOut: boolean;
  isError: boolean;
  onLogout: (sessionId: string) => void;
};

const channelLabel: Record<AdminUserSession['channel'], string> = {
  mobile: 'Mobile',
  web: 'Web',
  legacy: 'Legado',
};

const authenticationLabel: Record<NonNullable<AdminUserSession['authenticationMethod']>, string> = {
  password: 'Senha',
  google: 'Google',
  microsoft: 'Microsoft',
};

const formatDate = (timestamp: number | null) => (
  timestamp ? new Date(timestamp).toLocaleString('pt-BR') : 'Não informado'
);

export function SessionInfo({ sessions, isLoading, isLoggingOut, isError, onLogout }: SessionInfoProps) {
  if (isLoading) {
    return (
      <Stack alignItems="center" sx={{ py: 4 }}>
        <CircularProgress size={28} />
      </Stack>
    );
  }

  if (isError) {
    return <Typography color="error">Não foi possível carregar as sessões ativas.</Typography>;
  }

  if (!sessions.length) {
    return <Typography color="text.secondary">Este usuário não possui sessões ativas.</Typography>;
  }

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Sessões ativas encontradas: {sessions.length}
      </Typography>
      {sessions.map((session) => {
        const isMobile = session.channel === 'mobile';
        const authentication = session.authenticationMethod
          ? authenticationLabel[session.authenticationMethod]
          : 'Não informado';

        return (
          <Card key={session.id} variant="outlined" sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between">
              <Stack spacing={0.75}>
                <Stack direction="row" spacing={1} alignItems="center">
                  {isMobile ? <PhoneAndroidIcon color="primary" /> : <ComputerIcon color="primary" />}
                  <Typography fontWeight={700}>{channelLabel[session.channel]}</Typography>
                  <Chip size="small" color="success" label="Ativa" />
                </Stack>
                <Typography variant="body2">Autenticação: {authentication}</Typography>
                <Typography variant="body2">Última atividade: {formatDate(session.lastActivityAt)}</Typography>
                <Typography variant="body2">Criada em: {formatDate(session.createdAt)}</Typography>
                <Typography variant="body2">Expira em: {formatDate(session.expiresAt)}</Typography>
                {session.installationId && (
                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                    ID da instalação: {session.installationId}
                  </Typography>
                )}
              </Stack>
              <Button
                color="error"
                variant="contained"
                size="small"
                disabled={isLoggingOut}
                endIcon={<ExitToAppIcon />}
                onClick={() => onLogout(session.id)}
              >
                Encerrar sessão
              </Button>
            </Stack>
          </Card>
        );
      })}
    </Stack>
  );
}
